import { create } from 'zustand';
import { complaintsService, type Complaint, type ComplaintsFilters, type ComplaintsResponse } from '../services/complaintsService';
import { DEFAULT_COMPLAINTS_PAGE_SIZE } from '../constants/pagination';

interface ComplaintsState {
  // Данные
  complaints: Complaint[];
  currentComplaint: Complaint | null;
  total: number;
  page: number;
  size: number;
  totalPages: number;
  
  // Фильтры
  filters: ComplaintsFilters;
  
  // Состояние загрузки
  isLoading: boolean;
  isSubmitting: boolean;
  
  // Ошибки
  error: string | null;
  
  // Действия
  setFilters: (filters: Partial<ComplaintsFilters>) => void;
  loadComplaints: () => Promise<void>;
  loadComplaintById: (id: number) => Promise<void>;
  replyToComplaint: (complaintId: number, reply: string, status?: 'in_progress' | 'closed') => Promise<void>;
  updateComplaintStatus: (id: number, status: 'new' | 'in_progress' | 'closed') => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useComplaintsStore = create<ComplaintsState>((set, get) => ({
  // Начальное состояние
  complaints: [],
  currentComplaint: null,
  total: 0,
  page: 0,
  size: DEFAULT_COMPLAINTS_PAGE_SIZE,
  totalPages: 0,
  
  filters: {
    status: 'all',
    type: 'all',
    page: 0,
    size: DEFAULT_COMPLAINTS_PAGE_SIZE
  },
  
  isLoading: false,
  isSubmitting: false,
  error: null,
  
  // Установка фильтров
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters }
    }));
  },
  
  // Загрузка списка жалоб
  loadComplaints: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const { filters } = get();
      const response: ComplaintsResponse = await complaintsService.getComplaints(filters);
      
      set({
        complaints: response.items,
        total: response.total,
        page: response.page,
        size: response.size,
        totalPages: response.totalPages,
        isLoading: false
      });
    } catch (error) {
      console.error('Ошибка загрузки жалоб:', error);
      set({
        error: error instanceof Error ? error.message : 'Не удалось загрузить жалобы',
        isLoading: false
      });
    }
  },
  
  // Загрузка жалобы по ID
  loadComplaintById: async (id: number) => {
    set({ isLoading: true, error: null });
    
    try {
      const complaint = await complaintsService.getComplaintById(id);
      set({
        currentComplaint: complaint,
        isLoading: false
      });
    } catch (error) {
      console.error('Ошибка загрузки жалобы:', error);
      set({
        error: error instanceof Error ? error.message : 'Не удалось загрузить жалобу',
        isLoading: false
      });
    }
  },
  
  // Ответ на жалобу
  replyToComplaint: async (complaintId: number, reply: string, status?: 'in_progress' | 'closed') => {
    set({ isSubmitting: true, error: null });
    
    try {
      await complaintsService.replyToComplaint({
        complaintId,
        reply,
        status
      });
      
      // Перезагружаем список для обновления
      await get().loadComplaints();
      
    } catch (error) {
      console.error('Ошибка отправки ответа:', error);
      set({
        error: error instanceof Error ? error.message : 'Не удалось отправить ответ',
        isSubmitting: false
      });
    }
  },
  
  // Обновление статуса жалобы
  updateComplaintStatus: async (id: number, status: 'new' | 'in_progress' | 'closed') => {
    set({ isSubmitting: true, error: null });
    
    try {
      await complaintsService.updateComplaintStatus(id, status);
      
      // Обновляем жалобу в списке
      const { complaints } = get();
      const updatedComplaints = complaints.map(complaint => {
        if (complaint.id === id) {
          return { ...complaint, status };
        }
        return complaint;
      });
      
      set({
        complaints: updatedComplaints,
        isSubmitting: false
      });
      
    } catch (error) {
      console.error('Ошибка обновления статуса:', error);
      set({
        error: error instanceof Error ? error.message : 'Не удалось обновить статус',
        isSubmitting: false
      });
    }
  },
  
  // Очистка ошибки
  clearError: () => {
    set({ error: null });
  },
  
  // Сброс состояния
  reset: () => {
    set({
      complaints: [],
      currentComplaint: null,
      total: 0,
      page: 0,
      size: 10,
      totalPages: 0,
      filters: {
        status: 'all',
        type: 'all',
        page: 0,
        size: DEFAULT_COMPLAINTS_PAGE_SIZE
      },
      isLoading: false,
      isSubmitting: false,
      error: null
    });
  }
}));
